import { Box, Button, CircularProgress, IconButton, MenuItem, Snackbar, TextField, Typography } from '@mui/material';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import { useTheme } from '@emotion/react';
import { tokens } from '../../theme';

// Keep in sync with the backend limits in routes/excursionRoutes.js
const MAX_VIDEO_MB = 100;
const MAX_VIDEOS = 3;
const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.webm', '.mkv'];
const fileExtension = (name) => (name.match(/\.[^.]+$/)?.[0] || '').toLowerCase();

const ExcursionGallery = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [loading, setLoading] = useState(false);

  const [imageFiles, setImageFiles] = useState([]);
  const [imageUrls, setImageUrls] = useState([]);

  const [videoFiles, setVideoFiles] = useState([]);
  const [videoUrls, setVideoUrls] = useState([]);
  const [videoLoading, setVideoLoading] = useState(false);

  const [galleryImages, setGalleryImages] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [galleryIdToDelete, setGalleryIdToDelete] = useState(null);

  const token = localStorage.getItem('token');

  const showToast = (message) => {
    setToastmessage(message);
    setOpenToast(true);
  };

  const videoHandler = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length > MAX_VIDEOS) {
      showToast(`You can upload up to ${MAX_VIDEOS} videos at a time`);
      return;
    }
    const invalidType = files.find((file) => !VIDEO_EXTENSIONS.includes(fileExtension(file.name)));
    if (invalidType) {
      showToast(`"${invalidType.name}" is not supported. Please use MP4, MOV, WEBM or MKV videos.`);
      return;
    }
    const tooLarge = files.find((file) => file.size > MAX_VIDEO_MB * 1024 * 1024);
    if (tooLarge) {
      showToast(`"${tooLarge.name}" is larger than ${MAX_VIDEO_MB} MB`);
      return;
    }
    setVideoFiles(files);
    setVideoUrls(files.map((file) => URL.createObjectURL(file)));
  };

  const handleVideoSubmit = async (e) => {
    e.preventDefault();
    if (videoFiles.length === 0) {
      showToast('Please select video(s)');
      return;
    }

    setVideoLoading(true);
    const formData = new FormData();
    videoFiles.forEach((file) => formData.append('files', file));

    try {
      const uploadRes = await fetch(`${process.env.REACT_APP_APILINK}/excursionGallery/uploadgalleryvideos`, {
        headers: { token },
        method: 'POST',
        body: formData,
      });
      // A server size limit can answer with an HTML page instead of JSON.
      const uploadData = await uploadRes.json().catch(() => null);
      if (!uploadData) {
        showToast(
          uploadRes.status === 413 ? 'The video is too large for the server' : 'Video upload failed. Please try again.'
        );
        return;
      }
      if (!uploadData.success) {
        showToast(uploadData.message || 'Video upload failed');
        return;
      }

      const res = await fetch(`${process.env.REACT_APP_APILINK}/excursionGallery/addexcursionGallery`, {
        method: 'POST',
        headers: { 'Content-type': 'application/json', token },
        body: JSON.stringify({ images: uploadData.images, ExcursionID: id, mediaType: 'video' }),
      });
      const data = await res.json();
      if (data.message) showToast(data.message);
      if (data.success) {
        setVideoFiles([]);
        setVideoUrls([]);
        getGalleryImages();
      }
    } catch (error) {
      console.log(error);
      showToast('Video upload failed. Please try again.');
    } finally {
      setVideoLoading(false);
    }
  };

  const imageHandler = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
    const urls = files.map((file) => URL.createObjectURL(file));
    setImageUrls(urls);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (imageFiles.length > 0) {
      setLoading(true);

      const formData = new FormData();
      imageFiles.forEach((file) => {
        formData.append('files', file);
      });

      try {
        const imageRes = await fetch(`${process.env.REACT_APP_APILINK}/excursionGallery/uploadgalleryimages`, {
          headers: { token },
          method: 'POST',
          body: formData,
        });
        const imageData = await imageRes.json();

        if (imageData.message) {
          setOpenToast(true);
          setToastmessage(imageData.message);
        }
        if (imageData.success) {
          const res = await fetch(`${process.env.REACT_APP_APILINK}/excursionGallery/addexcursionGallery`, {
            method: 'POST',
            headers: {
              'Content-type': 'application/json',
              token,
            },
            body: JSON.stringify({ images: imageData.images, ExcursionID: id }),
          });
          const data = await res.json();

          if (data.message) {
            setOpenToast(true);
            setToastmessage(data.message);
          }
          if (data.success) {
            getGalleryImages();
          }
        }
      } catch (error) {
        console.log(error);
      }
    } else {
      setOpenToast(true);
      setToastmessage('Please Select image(s)');
    }
    setLoading(false);
  };

  const getGalleryImages = async () => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/excursionGallery/${id}`, { headers: { token } });
    const data = await res.json();
    if (data.images) {
      setGalleryImages(data.images);
    }
  };

  useEffect(() => {
    getGalleryImages();
  }, []);

  // Delete image
  const handleDeleteImage = async (galleryRowId) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/excursionGallery/${galleryRowId}`, {
      headers: { token },
      method: 'DELETE',
    });
    const data = await res.json();

    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
    }
    if (data.success) {
      setGalleryImages((prevImages) => prevImages.filter((image) => image.GalleryID !== galleryRowId));
    }
  };

  const openDeleteImageDialog = (galleryRowId) => {
    setGalleryIdToDelete(galleryRowId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteImageDialog = () => {
    setDeleteDialogOpen(false);
    setGalleryIdToDelete(null);
  };

  const confirmDeleteImage = async () => {
    if (galleryIdToDelete == null) return;
    await handleDeleteImage(galleryIdToDelete);
    closeDeleteImageDialog();
  };

  // Toast
  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" rowGap="10px" flexWrap="wrap-reverse">
        <Header title="GALLERY" subtitle="View Gallery" />
        <Link to={-1}>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': {
                backgroundColor: colors.blueAccent[800],
              },
            }}
          >
            Go Back
          </Button>
        </Link>
      </Box>

      <Box backgroundColor={colors.primary[400]} borderRadius={3} p="1.5rem">
        <Box display="grid" gap="10px">
          <Typography variant="h2" fontWeight={'bold'} color={colors.grey[100]} mb="5px">
            Upload Images
          </Typography>

          <form onSubmit={handleFormSubmit}>
            <Box>
              <label
                htmlFor="file-input"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'start',
                  flexWrap: 'wrap',
                  gap: '10px',
                  padding: '15px',
                  background: 'rgba(255,255,255,0.05)',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                {imageUrls.length > 0 ? (
                  imageUrls.map((imageUrl, index) => (
                    <img
                      key={index}
                      src={imageUrl}
                      className="addproduct-thumbnail-img"
                      alt={`Image Upload ${index}`}
                      style={{ width: '100%', maxWidth: '200px', objectFit: 'cover' }}
                    />
                  ))
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    <CloudUploadIcon fontSize="large" /> Upload Images
                  </Box>
                )}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={imageHandler}
                name="image"
                id="file-input"
                hidden
                multiple
              />

              <Button
                type="submit"
                disabled={loading}
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  marginBlock: '10px',
                  width: 'fit-content',
                  '&:hover': {
                    backgroundColor: colors.blueAccent[800],
                  },
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress color="secondary" size="20px" sx={{ marginRight: '10px' }} />
                    Uploading
                  </>
                ) : (
                  'Upload Images'
                )}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>

      {/* Upload Videos */}
      <Box backgroundColor={colors.primary[400]} borderRadius={3} p="1.5rem" mt="1.5rem">
        <Box display="grid" gap="10px">
          <Typography variant="h2" fontWeight={'bold'} color={colors.grey[100]} mb="5px">
            Upload Videos
          </Typography>
          <Box>
            <Typography variant="body2" color={colors.grey[300]}>
              Short clips only:
            </Typography>
            <Typography variant="body2" color={colors.grey[300]}>
              MP4, MOV, WEBM or MKV
            </Typography>
            <Typography variant="body2" color={colors.grey[300]}>
              Video size limit: {MAX_VIDEO_MB} MB
            </Typography>
          </Box>

          <form onSubmit={handleVideoSubmit}>
            <Box>
              <label
                htmlFor="video-input"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'start',
                  flexWrap: 'wrap',
                  gap: '10px',
                  padding: '15px',
                  background: 'rgba(255,255,255,0.05)',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                {videoUrls.length > 0 ? (
                  videoUrls.map((videoUrl, index) => (
                    <video
                      key={videoUrl}
                      src={videoUrl}
                      muted
                      style={{ width: '100%', maxWidth: '200px', aspectRatio: '16/9', objectFit: 'cover' }}
                      aria-label={`Video Upload ${index}`}
                    />
                  ))
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    <VideoLibraryIcon fontSize="large" sx={{ mr: '8px' }} /> Upload Videos
                  </Box>
                )}
              </label>
              <input
                type="file"
                accept={VIDEO_EXTENSIONS.join(',')}
                onChange={videoHandler}
                name="video"
                id="video-input"
                hidden
                multiple
              />

              <Button
                type="submit"
                disabled={videoLoading}
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: colors.grey[100],
                  fontSize: '14px',
                  fontWeight: 'bold',
                  padding: '10px 20px',
                  marginBlock: '10px',
                  width: 'fit-content',
                  '&:hover': {
                    backgroundColor: colors.blueAccent[800],
                  },
                }}
              >
                {videoLoading ? (
                  <>
                    <CircularProgress color="secondary" size="20px" sx={{ marginRight: '10px' }} />
                    Uploading
                  </>
                ) : (
                  'Upload Videos'
                )}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>

      {/* Show Gallery Images */}
      <Box backgroundColor={colors.primary[400]} borderRadius={3} p="1.5rem" mt="1.5rem">
        <Box display="grid" gap="10px" mb="2rem">
          <Typography variant="h2" fontWeight={'bold'} color={colors.grey[100]} mb="5px">
            Gallery Images & Videos
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
            }}
          >
            {galleryImages &&
              galleryImages.map((image, index) => (
                <div
                  key={index}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    textAlign: 'center',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
                  }}
                >
                  {image.mediaType === 'video' ? (
                    <video
                      src={image.imageURL}
                      controls
                      preload="metadata"
                      style={{
                        width: '100%',
                        maxWidth: '250px',
                        aspectRatio: '16/9',
                        objectFit: 'cover',
                        display: 'block',
                        background: '#000',
                      }}
                    />
                  ) : (
                    <img
                      src={image.imageURL}
                      style={{
                        width: '100%',
                        maxWidth: '250px',
                        aspectRatio: '16/9',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  )}
                  <Button
                    onClick={() => openDeleteImageDialog(image.GalleryID)}
                    sx={{
                      backgroundColor: colors.redAccent[500],
                      color: colors.grey[100],
                      fontSize: '14px',
                      fontWeight: 'bold',
                      padding: '5px 10px',
                      marginBlock: '10px',
                      width: 'fit-content',
                      '&:hover': {
                        backgroundColor: colors.redAccent[600],
                      },
                    }}
                  >
                    {image.mediaType === 'video' ? 'Remove Video' : 'Remove Image'}
                  </Button>
                </div>
              ))}
          </Box>
        </Box>
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteImageDialog}
        onConfirm={confirmDeleteImage}
        title="Remove from gallery?"
        message="Are you sure you want to remove this item from the excursion gallery?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ExcursionGallery;
